/**
 * Change history for a record: who changed what, when, and previous values.
 */
import { useState } from "react";
import { Button, Table } from "react-bootstrap";
import { useAuth } from "../lib/auth.tsx";
import { useFormat } from "../lib/format.ts";
import { useApi } from "../lib/query.ts";
import { Section } from "./ui/kit.tsx";

export interface AuditEntry {
  id: number;
  entityType: string;
  entityId: string | null;
  action: string;
  changes: Record<string, { from: unknown; to: unknown }>;
  summary: string;
  userName: string | null;
  createdAt: string;
}

function humanise(field: string): string {
  return field
    .replace(/Pence$/, "")
    .replace(/Date$/, " date")
    .replace(/([A-Z])/g, " $1")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
}

function show(field: string, value: unknown, money: (p: number) => string): string {
  if (value === null || value === undefined || value === "") return "—";
  if (field.endsWith("Pence") && typeof value === "number") return money(value);
  if (Array.isArray(value) || typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function ChangeList({ entry }: { entry: AuditEntry }) {
  const f = useFormat();
  const fields = Object.entries(entry.changes ?? {});
  if (entry.action !== "update" || fields.length === 0)
    return <span className="text-muted">{entry.summary}</span>;
  return (
    <ul className="list-unstyled mb-0 small">
      {entry.summary && <li className="text-muted">{entry.summary}</li>}
      {fields.map(([field, c]) => (
        <li key={field}>
          <strong>{humanise(field)}:</strong>{" "}
          <span className="text-decoration-line-through text-muted">
            {show(field, c.from, (p) => f.money(p))}
          </span>{" "}
          → {show(field, c.to, (p) => f.money(p))}
        </li>
      ))}
    </ul>
  );
}

export function AuditHistory({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const q = useApi<AuditEntry[]>(open && can("audit:read") ? "/api/audit" : null, { entityType, entityId });
  const f = useFormat();
  if (!can("audit:read")) return null;
  return (
    <Section
      title="Change history"
      actions={
        <Button size="sm" variant="link" onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"}
        </Button>
      }
    >
      {!open ? (
        <p className="small text-muted mb-0">
          Every change to this record is logged with the previous value.
        </p>
      ) : (
        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Action</th>
              <th>Changes</th>
            </tr>
          </thead>
          <tbody>
            {(q.data ?? []).map((e) => (
              <tr key={e.id}>
                <td className="text-nowrap">{f.dateTime(e.createdAt)}</td>
                <td>{e.userName ?? "System"}</td>
                <td className="text-capitalize">{e.action}</td>
                <td>
                  <ChangeList entry={e} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  );
}
